import { computed, IComputedValue } from 'mobx';
import loc from 'owa-localize';
import zip from 'lodash-es/zip';
import { ModeIndependentColor } from 'roosterjs-editor-types';
import {
    lightBlueColor,
    lightGreenColor,
    lightYellowColor,
    lightOrangeColor,
    lightRedColor,
    lightPurpleColor,
    blueColor,
    greenColor,
    yellowColor,
    orangeColor,
    redColor,
    purpleColor,
    darkBlueColor,
    darkGreenColor,
    darkYellowColor,
    darkOrangeColor,
    darkRedColor,
    darkPurpleColor,
    darkerBlueColor,
    darkerGreenColor,
    darkerYellowColor,
    darkerOrangeColor,
    darkerRedColor,
    darkerPurpleColor,
    whiteColor,
    lightGrayColor,
    grayColor,
    darkGrayColor,
    darkerGrayColor,
    blackColor,
    cyanColor,
    magentaColor,
    lightCyanColor,
    lightMagentaColor,
} from './Colors.locstring.json';

export interface ColorPickerItem {
    name: string;
    colorCode: ModeIndependentColor;
    borderColor?: ModeIndependentColor;
}

function makeColor(name: string, lightCode: string, darkCode: string): ColorPickerItem {
    return {
        name,
        colorCode: {
            lightModeColor: lightCode,
            darkModeColor: darkCode,
        },
        borderColor:
            lightCode === '#ffffff'
                ? {
                      lightModeColor: '#bebebe',
                      darkModeColor: '#212121',
                  }
                : undefined,
    };
}

const textColorNames = [
    loc(lightBlueColor),
    loc(lightGreenColor),
    loc(lightYellowColor),
    loc(lightOrangeColor),
    loc(lightRedColor),
    loc(lightPurpleColor),
    loc(blueColor),
    loc(greenColor),
    loc(yellowColor),
    loc(orangeColor),
    loc(redColor),
    loc(purpleColor),
    loc(darkBlueColor),
    loc(darkGreenColor),
    loc(darkYellowColor),
    loc(darkOrangeColor),
    loc(darkRedColor),
    loc(darkPurpleColor),
    loc(darkerBlueColor),
    loc(darkerGreenColor),
    loc(darkerYellowColor),
    loc(darkerOrangeColor),
    loc(darkerRedColor),
    loc(darkerPurpleColor),
    loc(whiteColor),
    loc(lightGrayColor),
    loc(grayColor),
    loc(darkGrayColor),
    loc(darkerGrayColor),
    loc(blackColor),
];
const lightModeTextColors = [
    '#51a7f9',
    '#6fc040',
    '#f5d427',
    '#f3901d',
    '#ed5c57',
    '#b36ae2',
    '#0c64c0',
    '#0c882a',
    '#dcbe22',
    '#de6a19',
    '#c82613',
    '#763e9b',
    '#174e86',
    '#0f5c1a',
    '#c3971d',
    '#be5b17',
    '#861106',
    '#5e327c',
    '#002451',
    '#06400c',
    '#a37519',
    '#934511',
    '#570606',
    '#3b204d',
    '#ffffff',
    '#cccccc',
    '#999999',
    '#666666',
    '#333333',
    '#000000',
];
const darkModeTextColors = [
    '#0075c2',
    '#207a00',
    '#5d4d00',
    '#ab5500',
    '#df504d',
    '#ab63da',
    '#6da0ff',
    '#3da848',
    '#6d5c00',
    '#d3610c',
    '#ff6847',
    '#d394f9',
    '#93b8f9',
    '#7fc57b',
    '#946f00',
    '#de7633',
    '#ff9b7c',
    '#dea9fd',
    '#cedbff',
    '#a3da9b',
    '#b5852a',
    '#ef935c',
    '#ffc0b1',
    '#eecaff',
    '#333333',
    '#535353',
    '#777777',
    '#a0a0a0',
    '#cfcfcf',
    '#ffffff',
];

let textColors = computed(() =>
    zip(textColorNames, lightModeTextColors, darkModeTextColors).map(set =>
        makeColor(set[0], set[1], set[2])
    )
) as IComputedValue<ColorPickerItem[]>;

export { textColors };

const backColorNames = [
    loc(cyanColor),
    loc(greenColor),
    loc(yellowColor),
    loc(orangeColor),
    loc(redColor),
    loc(magentaColor),
    loc(lightCyanColor),
    loc(lightGreenColor),
    loc(lightYellowColor),
    loc(lightOrangeColor),
    loc(lightRedColor),
    loc(lightMagentaColor),
    loc(whiteColor),
    loc(lightGrayColor),
    loc(grayColor),
    loc(darkGrayColor),
    loc(darkerGrayColor),
    loc(blackColor),
];

const lightModeBackColors = [
    '#00ffff',
    '#00ff00',
    '#ffff00',
    '#ff8000',
    '#ff0000',
    '#ff00ff',
    '#80ffff',
    '#80ff80',
    '#ffff80',
    '#ffc080',
    '#ff8080',
    '#ff80ff',
    '#ffffff',
    '#cccccc',
    '#999999',
    '#666666',
    '#333333',
    '#000000',
];
const darkModeBackColors = [
    '#005357',
    '#005e00',
    '#383e00',
    '#bf4c00',
    '#ff2711',
    '#e700e8',
    '#004c4f',
    '#005400',
    '#343c00',
    '#77480b',
    '#bc454a',
    '#aa2bad',
    '#333333',
    '#535353',
    '#777777',
    '#a0a0a0',
    '#cfcfcf',
    '#ffffff',
];

let backColors = computed(() =>
    zip(backColorNames, lightModeBackColors, darkModeBackColors).map(set =>
        makeColor(set[0], set[1], set[2])
    )
) as IComputedValue<ColorPickerItem[]>;

export { backColors };
